<template>
	<div class="org-menu">
		<h2 class="org-menu__heading sz-2 uppercase heavy mb-0 font-display">My Organizations</h2>
		<ol class="no-list-style mt-0 mb-3">
			<li
				v-for="o in orgs"
				:key="o.Id+'-name'"
				class="app-nav__org mb-0"
				:class="{'is-active': currentOrg && currentOrg.Id === o.Id, 'org-menu__disabled': o.Disabled }"
			>
				<CToolTip v-if="o.Disabled" placement="bottom" is-interactive class="w-full">
					<span v-if="o.IsBillingRestricted">{{ o.Name }} has an unpaid invoice and is currently restricted, which limits logins to Owners only. To have your access restored, please contact <a :href="`mailto:${o.FirstOwnerEmail}`">{{ o.FirstOwnerEmail }}</a>.</span>
					<span v-else>Your account no longer has access to this organization. Please contact <a :href="`mailto:${o.FirstOwnerEmail}`">{{ o.FirstOwnerName }}</a> to restore access to this organization.</span>
					<template #button>
						<OrgMenuItem
							:org="o"
							:is-current="currentOrg && currentOrg.Id === o.Id"
							:is-admin="isAdmin"
							@hover="handleHover(o.Id)"
							@org-click="handleOrgClick(o, false)"
							@settings-click="handleOrgClick(o, true)"
						/>
					</template>
				</CToolTip>
				<OrgMenuItem
					v-else
					:org="o"
					:is-current="currentOrg && currentOrg.Id === o.Id"
					:is-admin="isAdmin"
					@hover="handleHover(o.Id)"
					@org-click="handleOrgClick(o, false)"
					@settings-click="handleOrgClick(o, true)"
				/>
			</li>
		</ol>
		<CIconButton
			v-if="isVerified"
			is-block
			class="org-menu__new-org-button"
			data-cf-start="CreateOrganization"
			data-cf-variant="AdditionalOrg"
			data-cf-entry-point="OrgMenu"
			@click="handleNewOrgClick"
		>
			New Organization
		</CIconButton>
		<CCreateNewOrganizationDialog
			:email-address="emailAddress"
			:show="showNewOrgDialog"
			@close="handleCreateDialog"
		/>
	</div>
</template>

<script lang="ts">
	import CIconButton from 'src/components/interaction/IconButton.vue';
	import CCreateNewOrganizationDialog from 'src/components/dialogs/CreateNewOrganizationDialog.vue';
	import { globalStore } from 'src/stores/global-store';
	import type { PropType } from 'vue';
	import { defineComponent, ref, computed } from 'vue';
	import { useRouter } from 'vue-router/composables';
	import type { OrganizationAccessMeta } from './AccountAndOrgFlyout.vue';
	import type { OrganizationMeta } from '@cognitoforms/types/server-types/core/services/organization-meta';
	import CToolTip from 'src/components/interaction/ToolTip.vue';
	import OrgMenuItem from './OrgMenuItem.vue';

	export default defineComponent({
		name: 'COrgMenu',
		components: { CIconButton, CCreateNewOrganizationDialog, CToolTip, OrgMenuItem },
		props: {
			orgs: {
				type: Array as PropType<OrganizationAccessMeta[]>,
				default: null
			},
			currentOrg: {
				type: Object as PropType<OrganizationMeta>,
				default: null
			},
			isAdmin: {
				type: Boolean,
				default: true
			},
			orgAccessPromises: {
				type: Map as PropType<Map<string, Promise<any>>>,
				default: () => new Map<string, Promise<any>>()
			}
		},
		emits: ['close-side-menus', 'check-org-access'],
		setup(props, { emit }) {
			const router = useRouter();
			const showNewOrgDialog = ref(false);
			const isVerified = computed(() => globalStore.user.IsVerified);
			const emailAddress = computed(() => globalStore.user.Email);

			const handleNewOrgClick = () => {
				showNewOrgDialog.value = true;
			};

			const handleOrgClick = async (org, goToSettings) => {
				// Make sure call to check access resolves before navigating
				await props.orgAccessPromises[org.Id];
				if (!props.orgs.find(o => o.Id === org.Id).Disabled) {
					emit('close-side-menus');
					const path = `/${org.Code}${goToSettings ? '/settings' : ''}`;
					if (org.Id === props.currentOrg.Id)
						router.push(path);
					else
						window.location.assign(path);
				}
			};

			const handleCreateDialog = () => {
				showNewOrgDialog.value = !showNewOrgDialog.value;
			};

			const handleHover = (orgId: string) => {
				emit('check-org-access', orgId);
			};

			return {
				showNewOrgDialog,
				isVerified,
				emailAddress,
				handleNewOrgClick,
				handleCreateDialog,
				handleHover,
				handleOrgClick
			};
		}
	});
</script>

<style lang="scss">
.org-menu {
	padding-bottom: env(safe-area-inset-bottom, 0px);

	&__heading,
	a:not(.org-menu__settings-button),
	button:not(.el-tooltip),
	&__new-org-button {
		padding-right: $space__4;
		padding-left: $space__4;
	}

	&__new-org-button {
		padding-left: $space__4 - 4px;
	}

	&__heading {
		padding-top: $space__4;
		padding-bottom: $space__3;
	}

	a:not(.org-menu__settings-button, .el-tooltip) {
		text-align: left;
	}

	:root:root &__disabled {
		background-color: var(--bg-disabled);
		cursor: not-allowed;

		.org-menu__org-button {
			color: var(--text-disabled);
			pointer-events: none;
		}

		button {
			cursor: not-allowed;
		}
	}

	&__settings-button {

		[data-admin-layout] & {
			align-self: stretch;
			border-left: 1px solid transparent;

			&:hover,
			.org-menu__org-button:hover + & {
				border-color: var(--shade-1);
			}
		}

		[data-app-layout] & {
			border-radius: $border-radius__text-button;
			margin-right: $space__3;
			padding: var(--icon-button-padding);
			transition: background $speed__hovered;

			i {
				color: var(--icon-subtle);
			}

			&:hover {
				background: var(--bg-hover-in-selected);

				i {
					color: var(--icon-intense);
				}
			}
		}
	}

	&__org-button {

		[data-admin-layout] & {
			padding-top: $space__touch-target;
			padding-bottom: $space__touch-target;
		}

		[data-app-layout] & {
			min-height: var(--menu-item-height);
		}
	}

	&__org-icon {
		margin-right: $space__icon;
		color: var(--icon-subtle);

		.is-active & {
			color: inherit;
		}
	}

	&__settings-button {
		display: flex;
		align-items: center;
		justify-content: center;

		[data-admin-layout] & {
			width: 50px;
		}
	}

	.is-active {

		a {

			[data-admin-layout] & {
				background: var(--bg-selected-nav);
				font-weight: $weight__text--medium;

				&:hover {
					background: var(--shade-3);
				}
			}
		}
	}

	li:hover {
		background: var(--bg-hovered);
	}

	li.is-active {
		background: var(--bg-selected);

		[data-app-layout] & {
			@include selected-menu-item;
		}
	}
}
</style>